import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { piePng } from "./reportPieImage";

function stampFetched(pdf, fetchedAt) {
  const label = `Fetched: ${fetchedAt || new Date().toLocaleString()}`;
  const pages = pdf.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    pdf.setPage(page);
    const width = pdf.internal.pageSize.getWidth();
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, width, 8, "F");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(55, 65, 81);
    pdf.text(label, width - 8, 5.5, { align: "right" });
  }
}

export function pdfCell(value, column) {
  if (value == null || value === "") return "-";
  if (column?.format === "battery") {
    const number = Number(value);
    if (!Number.isFinite(number)) return String(value);
    return `${Number.isInteger(number) ? number : number.toFixed(1)}%`;
  }
  const text = String(value).trim() || "-";
  return column?.suffix ? `${text}${column.suffix}` : text;
}

export async function downloadReportViewPdf(view, fetchedAt) {
  const pdf = new jsPDF("l", "mm", "a4");
  const margin = 10;
  const contentTop = 14;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - margin * 2;
  let y = contentTop;

  const ensure = (height) => {
    if (y + height > pageHeight - margin) {
      pdf.addPage();
      y = contentTop;
    }
  };
  const heading = (text, size = 14) => {
    ensure(size * 0.6 + 4);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(size);
    pdf.setTextColor(27, 37, 89);
    pdf.text(String(text || ""), margin, y);
    y += size * 0.45 + 3;
  };
  const line = (text) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(55, 65, 81);
    const lines = pdf.splitTextToSize(String(text || ""), contentWidth);
    ensure(lines.length * 5 + 1);
    pdf.text(lines, margin, y);
    y += lines.length * 5 + 1;
  };
  const table = (headers, rows, widths) => {
    const rowHeight = 7;
    const drawHeader = () => {
      ensure(rowHeight + 1);
      pdf.setFillColor(66, 170, 204);
      pdf.rect(margin, y - 4.5, contentWidth, rowHeight, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(255, 255, 255);
      let x = margin + 1.5;
      headers.forEach((header, index) => {
        pdf.text(String(header), x, y);
        x += widths[index];
      });
      y += rowHeight;
    };
    drawHeader();
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(27, 37, 89);
    if (rows.length === 0) {
      ensure(rowHeight);
      pdf.text("No rows.", margin + 1.5, y);
      y += rowHeight + 3;
      return;
    }
    rows.forEach((row, rowIndex) => {
      if (y + rowHeight > pageHeight - margin) {
        pdf.addPage();
        y = contentTop;
        drawHeader();
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(27, 37, 89);
      }
      if (rowIndex % 2 === 0) {
        pdf.setFillColor(244, 247, 254);
        pdf.rect(margin, y - 4.5, contentWidth, rowHeight, "F");
      }
      let x = margin + 1.5;
      row.forEach((value, index) => {
        const fitted = pdf.splitTextToSize(String(value), Math.max(widths[index] - 2, 4))[0] || "";
        pdf.text(fitted, x, y);
        x += widths[index];
      });
      y += rowHeight;
    });
    y += 3;
  };

  const fetchedLabel = fetchedAt || view?.generatedAt || new Date().toLocaleString();
  heading(view?.title || "Report", 16);
  line(`Fetched: ${fetchedLabel}`);
  if (view?.summary) line(view.summary);
  (view?.charts || []).forEach((chart) => {
    heading(chart.title || "Chart", 12);
    const slices = chart.slices || [];
    if (slices.length > 0) {
      const imgWidth = 120;
      const imgHeight = 70;
      ensure(imgHeight + 2);
      pdf.addImage(piePng(slices), "PNG", margin, y, imgWidth, imgHeight);
      y += imgHeight + 4;
    }
    table(
      ["Item", "Count", "Detail"],
      slices.map((slice) => [slice.label || "", String(slice.value ?? 0), slice.detail || ""]),
      [80, 30, contentWidth - 110]
    );
  });
  (view?.tables || []).forEach((dataTable) => {
    const columns = dataTable.columns || [];
    if (columns.length === 0) return;
    heading(dataTable.title || "Table", 12);
    if (dataTable.subtitle) line(dataTable.subtitle);
    const width = contentWidth / columns.length;
    table(
      columns.map((column) => column.label || column.key),
      (dataTable.rows || []).map((row) => columns.map((column) => pdfCell(row[column.key], column))),
      columns.map(() => width)
    );
  });

  stampFetched(pdf, fetchedLabel);
  const fileName = String(view?.title || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "report";
  pdf.save(`${fileName}.pdf`);
}

export async function downloadVisibleReportPdf(element, fetchedAt) {
  const canvas = await html2canvas(element, { scale: 2, useCORS: true });
  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgHeight = (canvas.height * pageWidth) / canvas.width;
  let heightLeft = imgHeight;
  let position = 0;

  pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
  heightLeft -= pageHeight;
  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
    heightLeft -= pageHeight;
  }
  stampFetched(pdf, fetchedAt);
  pdf.save("report.pdf");
}
